// Properties utilities - matching admin-web structure
import { ICreateProperty, IPropertyFormValues, ICreatePropertyPhoto } from '@/types/admin/properties';

/**
 * Get today's date in ISO format for filters
 */
export const getTodayFilterValue = (): string => {
  const now = new Date();
  const offset = now.getTimezoneOffset();
  const initialFilterDate = new Date(now.getTime() - offset * 60 * 1000);
  return initialFilterDate.toISOString().split('T')[0];
};

/**
 * Convert form values to property API payload
 */
export const convertFormValuesToProperty = (
  formValues: Record<string, unknown>
): ICreateProperty => {
  const {
    marketingName,
    externalId,
    organizationId,
    photos,
    propertyEmailAddress,
    propertyPhoneNumber,
    propertyUrl,
    propertyFaqUrl,
    propertyResidentHandbook,
    propertyCbxFilePath,
    propertyNsfFee,
    propertyLateDayStart,
    propertyLateDayFee,
    propertyLateDailyFee,
    propertyMaxMonthlyFee,
    propertyCashAccountId,
    accountsReceivableToResioAccountId,
    convenienceFeeAccountId,
    creditCardMerchantAccountId,
    achMerchantAccountId,
  } = formValues as unknown as IPropertyFormValues;

  return {
    marketingName,
    externalId,
    organizationId,
    photos,
    propertyEmailAddress,
    propertyPhoneNumber,
    propertyUrl,
    propertyFaqUrl,
    propertyResidentHandbook,
    propertyCbxFilePath,
    propertyNsfFee,
    propertyLateDayStart,
    propertyLateDayFee,
    propertyLateDailyFee,
    propertyMaxMonthlyFee,
    propertyCashAccountId,
    accountsReceivableToResioAccountId,
    convenienceFeeAccountId,
    creditCardMerchantAccountId,
    achMerchantAccountId,
  };
};

/**
 * Convert form values to FormData for file uploads
 */
export const convertToFormData = (formValues: Record<string, unknown>): FormData => {
  const {
    marketingName,
    externalId,
    organizationId,
    photos,
    propertyEmailAddress,
    propertyPhoneNumber,
    propertyUrl,
    propertyFaqUrl,
    propertyResidentHandbook,
    propertyCbxFilePath,
    propertyNsfFee,
    propertyLateDayStart,
    propertyLateDayFee,
    propertyLateDailyFee,
    propertyMaxMonthlyFee,
  } = formValues as unknown as IPropertyFormValues;

  const formData = new FormData();
  formData.append('name', marketingName);
  formData.append('externalId', externalId.toString());
  formData.append('organizationId', organizationId.toString());
  formData.append('propertyEmailAddress', propertyEmailAddress);
  formData.append('propertyPhoneNumber', propertyPhoneNumber);
  formData.append('propertyUrl', propertyUrl);

  if (propertyFaqUrl) {
    formData.append('propertyFaqUrl', propertyFaqUrl);
  }
  if (propertyResidentHandbook) {
    formData.append('propertyResidentHandbook', propertyResidentHandbook);
  }
  if (propertyCbxFilePath) {
    formData.append('propertyCbxFilePath', propertyCbxFilePath);
  }

  formData.append('propertyNsfFee', propertyNsfFee.toString());
  if (propertyLateDayStart !== undefined) {
    formData.append('propertyLateDayStart', propertyLateDayStart.toString());
  }
  formData.append('propertyLateDayFee', propertyLateDayFee.toString());
  formData.append('propertyLateDailyFee', propertyLateDailyFee.toString());
  formData.append('propertyMaxMonthlyFee', propertyMaxMonthlyFee.toString());

  // Add photos if present
  photos?.forEach((photo: ICreatePropertyPhoto) => {
    formData.append('photos', photo.file);
  });

  return formData;
};

/**
 * Format property CSV data
 */
export const formatPropertyCsvData = (data: any): (string | number)[][] => {
  const rowHeaders = ['marketingName', 'externalId', 'address'];

  if (!data || !data.response || !data.response.rows) {
    return [rowHeaders];
  }

  const listData = data.response.rows.map((row: any) => [
    row.marketingName,
    row.externalId,
    row.address || '',
  ]);

  return [rowHeaders, ...listData];
};

